import type { components } from '@trackercop/contracts';
import { Badge } from '@/components/ui/badge';

type Baseline = components['schemas']['Baseline'];
type DriftReport = components['schemas']['DriftReport'];

interface BaselineStatusBadgeProps {
  baseline: Baseline | null | undefined;
  drift: DriftReport | null | undefined;
}

// Three-state indicator used on the Sites list: "current" (baseline
// exists and no drift), "drifted" (baseline exists and drift report
// flags changes), "no baseline" (reviewer has never approved a baseline).
// The signal a reviewer cares about is "is the site's approved shape
// still what we're observing?"; this badge answers that at a glance.
export function BaselineStatusBadge({ baseline, drift }: BaselineStatusBadgeProps) {
  if (!baseline) return <Badge variant="muted">No baseline</Badge>;
  if (!drift) return <Badge variant="success">Current</Badge>;
  const drifted =
    (drift.newScripts?.length ?? 0) > 0 ||
    (drift.removedScripts?.length ?? 0) > 0 ||
    (drift.driftedScripts?.length ?? 0) > 0 ||
    (drift.newDestinations?.length ?? 0) > 0 ||
    (drift.newPayloadCategories?.length ?? 0) > 0 ||
    (drift.newGTMTags?.length ?? 0) > 0;
  return drifted ? <Badge variant="warning">Drifted</Badge> : <Badge variant="success">Current</Badge>;
}

interface DriftSeverityBadgeProps {
  severity: 'none' | 'minor' | 'major' | null;
}

export function DriftSeverityBadge({ severity }: DriftSeverityBadgeProps) {
  if (!severity || severity === 'none') return <Badge variant="muted">None</Badge>;
  if (severity === 'minor') return <Badge variant="warning">Minor</Badge>;
  return <Badge variant="destructive">Major</Badge>;
}
